import { useState } from 'react';

/**
 * Custom hook for managing alert dialogs
 * 
 * @returns {Object} Alert dialog state and handlers
 */
const useAlertDialog = () => {
  const [alertState, setAlertState] = useState({
    open: false,
    type: 'success',
    title: '',
    message: '',
    confirmText: '',
    onConfirm: null,
    autoClose: true
  });

  /**
   * Show success alert
   * @param {string} message - Success message
   * @param {Object} options - Additional options
   */
  const showSuccess = (message, options = {}) => {
    setAlertState({
      open: true,
      type: 'success',
      message,
      autoClose: true,
      ...options
    });
  };

  /**
   * Show error alert
   * @param {string} message - Error message
   * @param {Object} options - Additional options
   */
  const showError = (message, options = {}) => {
    setAlertState({
      open: true,
      type: 'error',
      message,
      autoClose: false,
      ...options
    });
  };

  /**
   * Show warning alert
   * @param {string} message - Warning message
   * @param {Object} options - Additional options
   */
  const showWarning = (message, options = {}) => {
    setAlertState({
      open: true,
      type: 'warning',
      message,
      autoClose: false,
      ...options
    });
  };

  /**
   * Show info alert
   * @param {string} message - Info message
   * @param {Object} options - Additional options
   */
  const showInfo = (message, options = {}) => {
    setAlertState({
      open: true,
      type: 'info',
      message,
      autoClose: false,
      ...options
    });
  };

  /**
   * Close alert dialog
   */
  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, open: false }));
  };

  return {
    alertState,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeAlert
  };
};

export default useAlertDialog;